import { CurrencyView } from '../currency-view';
import { Slider } from '../slider';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { handleKeyDownDisableNumber } from '@/client/utils/handle';
import { amountSchema } from '@/client/utils/schema';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUnits, parseUnits } from 'viem';

type TreadInputProps = {
  disabled?: boolean;
  type: 'buy' | 'sell';
  value?: bigint;
  maxAmount?: bigint; // eth
  onValueChange?: (amount?: bigint) => void;
  isLoading?: boolean;
  decimal: number;
  // When false, hide balance/max-hint UI (used for unauthenticated users so
  // they can still enter an amount but aren't confused by a "Balance 0" line).
  showBalance?: boolean;
};

const formatInitNumber = (input: bigint | string) => {
  const match = input.toString().match(/^\d+(\.\d{1,2})?/);
  if (!match) return '';
  const result = match[0];
  return result.replace(/\.0+$/, '');
};

export const TreadInput = ({
  disabled,
  type,
  value,
  maxAmount,
  onValueChange,
  isLoading,
  decimal,
  showBalance = true,
}: TreadInputProps) => {
  const { t } = useTranslation();
  const initValue = useMemo(() => (value ? formatInitNumber(formatUnits(value, decimal)) : ''), [decimal, value]);
  const maxValue = useMemo(() => (maxAmount ? formatUnits(maxAmount, decimal) : 0), [decimal, maxAmount]);
  const [inputAmount, setInputAmount] = useState<string>(initValue);
  useEffect(() => {
    if (inputAmount !== initValue) setInputAmount(initValue);
  }, [initValue]);
  const quickerInputs = [
    {
      label: '+1',
      value: 1,
      onClick: () => {
        const amount = `${Number(inputAmount ?? 0) + 1}`;
        setInputAmount(amount);
      },
    },
    {
      label: '+10',
      value: 10,
      onClick: () => {
        const amount = `${Number(inputAmount ?? 0) + 10}`;
        setInputAmount(amount);
      },
    },
    // Only show Max when a balance exists; unauth users have no balance to max.
    ...(showBalance
      ? [
          {
            label: t('max'),
            value: maxValue,
            onClick: () => {
              const amount = maxValue ? Math.floor(Number(maxValue) * 1e2) / 1e2 : '';
              setInputAmount(amount?.toString());
            },
          },
        ]
      : []),
  ];
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const { success } = amountSchema.safeParse(input);
    if (success || input == '') {
      const decimalParts = input.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > 2) return;
      setInputAmount(input);
    }
  };

  useEffect(() => {
    onValueChange?.(inputAmount ? parseUnits(inputAmount, decimal) : undefined);
  }, [decimal, inputAmount, onValueChange]);

  return (
    <>
      <div className="mt-2 pos-relative">
        <Input
          type="text"
          disabled={disabled}
          value={inputAmount}
          onKeyDown={handleKeyDownDisableNumber}
          onChange={handleChange}
          placeholder="0"
          className="border border-foreground/10 focus:border-foreground/40 py-4 h-14 !text-8 leading-8 placeholder:leading-8 placeholder:text-8 pe-33 shadow-none"
        />
        <div className="pos-absolute top-3 flex items-center gap-2 end-2">
          {quickerInputs.map((item, index) => (
            <Button
              key={`quicker-input-${index}`}
              variant="secondary"
              size="sm"
              className="!rounded-2 min-w-8 p-2 bg-foreground/4 text-foreground/70 hover:bg-primary hover:text-primary-foreground"
              disabled={disabled}
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>
      {showBalance && (
        <div className="flex items-center justify-between mt-2 gap-8">
          <div className="flex items-center gap-1 flex-1 flex-shrink-0 leading-3 text-sm  text-foreground/70 ">
            <span className="whitespace-nowrap">
              {type == 'buy' && t('balance')}
              {type == 'sell' && t('available')}
            </span>
            <CurrencyView showIcon={type === 'buy'} animated={true} value={maxValue} isLoading={isLoading} decimal={0} />
          </div>
          <Slider
            className="w-33"
            disabled={disabled}
            value={Number(inputAmount)}
            min={0}
            max={Number(maxValue)}
            setValue={value => setInputAmount(`${Math.floor(Number(value) * 1e2) / 1e2}`)}
          />
        </div>
      )}
    </>
  );
};
